import { z } from 'zod';

// Shared schemas and types for CRM Connections across app-v1 and api-v1

export const crmStatusSchema = z.enum(['active', 'inactive', 'error']);

export const crmConnectionSchema = z.object({
	id: z.number(),
	identifier: z.string(),
	name: z.string(),
	description: z.string().nullable().optional(),
	providerId: z.string(),
	providerName: z.string(),
	isAuthenticated: z.boolean(),
	status: crmStatusSchema,
	errorMessage: z.string().nullable().optional(),
	lastSyncedAt: z.coerce.date().nullable().optional(),
	createdOn: z.coerce.date(),
	updatedOn: z.coerce.date(),
	createdBy: z.object({ id: z.number(), name: z.string().nullable().optional() }).nullable().optional(),
	// Optional fields present in backend responses; UI can ignore if not needed
	config: z.record(z.any()).optional(),
	secretMappings: z.record(z.string()).optional(),
});

export const listCrmConnectionsResponseSchema = z.object({
	items: z.array(crmConnectionSchema),
});

export const createCrmConnectionBodySchema = z.object({
	name: z.string().min(2),
	description: z.string().optional(),
	providerId: z.string(),
	providerName: z.string(),
	config: z.record(z.string()).optional(),
	secretMappings: z.record(z.string()).optional(),
});

export const updateCrmConnectionBodySchema = z.object({
	name: z.string().min(2).optional(),
	description: z.string().optional(),
	config: z.record(z.string()).optional(),
	secretMappings: z.record(z.string()).optional(),
});

export const identifyCrmConnectionParamsSchema = z.object({
	crmConnectionIdentifier: z.string(),
});

export const crmAuthTypeSchema = z.enum(['oauth', 'apiKey', 'basic']);
export const crmProviderConfigFieldSchema = z.object({
	name: z.string(),
	label: z.string(),
	type: z.enum(['text','password','url','select','secret','boolean']),
	required: z.boolean().default(false),
	isSensitive: z.boolean().default(false),
	options: z.array(z.object({ value: z.string(), label: z.string() })).optional(),
	position: z.number().default(0),
	helpText: z.string().optional(),
});

export const crmProviderSchema = z.object({
	id: z.string(),
	name: z.string(),
	description: z.string().optional(),
	authType: crmAuthTypeSchema,
	logo: z.string().optional(),
	configFields: z.array(crmProviderConfigFieldSchema),
	conventions: z.record(z.any()).optional(),
	apiRoutes: z.record(z.any()).optional(),
});
export const listCrmProvidersResponseSchema = z.object({ items: z.array(crmProviderSchema) });

export const crmTestConfigSchema = z.object({
	method: z.string().default('GET'),
	url: z.string().optional(),
	headers: z.record(z.any()).optional(),
	body: z.any().optional(),
	successCriteria: z.any().optional(),
});
export const getCrmTestConfigResponseSchema = z.object({ test: crmTestConfigSchema.nullable() });
export const upsertCrmTestConfigBodySchema = crmTestConfigSchema;

export const crmObjectSchema = z.object({
	id: z.string(),
	name: z.string(),
	label: z.string(),
	description: z.string().optional(),
	type: z.enum(['standard', 'custom']).default('standard'),
	fieldsCount: z.number().default(0),
});
export const listCrmObjectsResponseSchema = z.object({ items: z.array(crmObjectSchema) });
export const getCrmObjectFieldsResponseSchema = z.object({ fields: z.array(z.any()) });

// api-v1: Create CRM Record
export const createCrmRecordRequestSchema = z.object({
	formIdentifier: z.string(),
	objectName: z.string().optional(),
	values: z.record(z.any()),
});
export const createCrmRecordResponseSchema = z
	.object({ id: z.string().optional(), success: z.boolean().optional(), errors: z.array(z.any()).optional() })
	.passthrough();

export type CrmStatus = z.infer<typeof crmStatusSchema>;
export type CrmConnection = z.infer<typeof crmConnectionSchema>;
export type ListCrmConnectionsResponse = z.infer<typeof listCrmConnectionsResponseSchema>;
export type CreateCrmConnectionBody = z.infer<typeof createCrmConnectionBodySchema>;
export type UpdateCrmConnectionBody = z.infer<typeof updateCrmConnectionBodySchema>;
export type CrmAuthType = z.infer<typeof crmAuthTypeSchema>;
export type CrmProviderConfigField = z.infer<typeof crmProviderConfigFieldSchema>;
export type CrmProvider = z.infer<typeof crmProviderSchema>;
export type ListCrmProvidersResponse = z.infer<typeof listCrmProvidersResponseSchema>;
export type CrmTestConfig = z.infer<typeof crmTestConfigSchema>;
export type GetCrmTestConfigResponse = z.infer<typeof getCrmTestConfigResponseSchema>;
export type CrmObject = z.infer<typeof crmObjectSchema>;
export type ListCrmObjectsResponse = z.infer<typeof listCrmObjectsResponseSchema>;
export type CreateCrmRecordRequest = z.infer<typeof createCrmRecordRequestSchema>;
export type CreateCrmRecordResponse = z.infer<typeof createCrmRecordResponseSchema>;


